/**
 * Mapas rota → contexto de medição.
 *
 * O lookup é por **rota exata**. Nunca enviamos pathname bruto nem query
 * string ao GA4: a rota é traduzida para um valor de enum antes de sair daqui.
 */

import { BUSINESS_SEGMENTS, CTA_PAGES } from "./constants";

/** Rota exata → `cta_page`. */
export const ROUTE_TO_PAGE = Object.freeze({
  "/": CTA_PAGES.HOME,
  "/agendamento": CTA_PAGES.AGENDAMENTO,
  "/atendimento": CTA_PAGES.ATENDIMENTO,
  "/barbearias": CTA_PAGES.BARBEARIAS,
  "/consultorios": CTA_PAGES.CONSULTORIOS,
  "/mecanicas": CTA_PAGES.MECANICAS,
  "/lava-jatos": CTA_PAGES.LAVA_JATOS,
  "/academias": CTA_PAGES.ACADEMIAS,
  "/recursos": CTA_PAGES.RECURSOS,
});

/** Rota exata → `business_segment`. */
export const ROUTE_TO_SEGMENT = Object.freeze({
  "/": BUSINESS_SEGMENTS.GENERAL,
  // As duas páginas de produto são institucionais, não de segmento: vendem uma
  // CAPACIDADE (agendar / atender), atravessando barbearia, consultório etc.
  // Quem quer recortar por produto usa a dimensão `product`.
  "/agendamento": BUSINESS_SEGMENTS.GENERAL,
  "/atendimento": BUSINESS_SEGMENTS.GENERAL,
  "/barbearias": BUSINESS_SEGMENTS.BARBEARIAS,
  "/consultorios": BUSINESS_SEGMENTS.CONSULTORIOS,
  "/mecanicas": BUSINESS_SEGMENTS.MECANICAS,
  "/lava-jatos": BUSINESS_SEGMENTS.LAVA_JATOS,
  "/academias": BUSINESS_SEGMENTS.ACADEMIAS,
  "/recursos": BUSINESS_SEGMENTS.GENERAL,
});

/**
 * Normaliza o pathname antes do lookup: descarta query string e hash,
 * remove barra final e uniformiza caixa. O resultado NUNCA é enviado ao
 * GA4 — serve só de chave para o mapa.
 *
 * @param {string} pathname
 * @returns {string}
 */
function normalizePath(pathname) {
  if (typeof pathname !== "string") return "";

  let path = pathname;

  const queryIndex = path.indexOf("?");
  if (queryIndex !== -1) path = path.slice(0, queryIndex);

  const hashIndex = path.indexOf("#");
  if (hashIndex !== -1) path = path.slice(0, hashIndex);

  path = path.trim().toLowerCase();

  // Barra final não muda a rota: `/barbearias/` === `/barbearias`.
  while (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }

  return path;
}

/**
 * Resolve o contexto de medição a partir da rota atual.
 * Rota desconhecida cai em `unknown` / `general` — nunca vaza o pathname.
 *
 * @param {string} pathname
 * @returns {{ cta_page: string, business_segment: string }}
 */
export function resolveContextFromPath(pathname) {
  const path = normalizePath(pathname);

  // `hasOwnProperty` de propósito: sem ele, uma rota chamada `/constructor`
  // resolveria para uma propriedade herdada do prototype.
  const hasPage = Object.prototype.hasOwnProperty.call(ROUTE_TO_PAGE, path);
  const hasSegment = Object.prototype.hasOwnProperty.call(
    ROUTE_TO_SEGMENT,
    path,
  );

  return {
    cta_page: hasPage ? ROUTE_TO_PAGE[path] : CTA_PAGES.UNKNOWN,
    business_segment: hasSegment
      ? ROUTE_TO_SEGMENT[path]
      : BUSINESS_SEGMENTS.GENERAL,
  };
}
